// src/components/WorkflowRunModal.jsx
import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { workflowService } from '../services/workflowService';
import { webhookService } from '../services/webhookService';
import {
  XMarkIcon, PlayIcon, ArrowPathIcon, CheckCircleIcon,
  ExclamationTriangleIcon, ClockIcon,
  PlayCircleIcon, BookOpenIcon, CircleStackIcon, GlobeAltIcon,
  ArrowsRightLeftIcon, SparklesIcon, CodeBracketIcon, CpuChipIcon,
  FunnelIcon, StopCircleIcon, WrenchScrewdriverIcon, ChatBubbleLeftRightIcon,
  ClipboardDocumentIcon, LinkIcon, DocumentChartBarIcon, DocumentTextIcon,
  DocumentArrowDownIcon, TableCellsIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import './WorkflowRunModal.css';

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#0ea5e9'];

/* ─── Report output renderer ─────────────────────────────────────────────── */
const ReportOutput = ({ step }) => {
  const { render_type, chart_type, title, data = [], x_field, y_field, columns = [] } = step;

  if (!data.length) return <div className="wrm-report-empty">No data to display.</div>;

  if (render_type === 'table') {
    const cols = columns.length ? columns : Object.keys(data[0] || {});
    return (
      <div className="wrm-report-table-wrap">
        {title && <div className="wrm-report-title">{title}</div>}
        <div className="wrm-report-table-scroll">
          <table className="wrm-report-table">
            <thead>
              <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>{cols.map(c => <td key={c}>{row[c] ?? '—'}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Chart
  const ct = chart_type || 'bar';
  if (ct === 'pie') {
    return (
      <div className="wrm-report-chart">
        {title && <div className="wrm-report-title">{title}</div>}
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={data} dataKey={y_field} nameKey={x_field} cx="50%" cy="50%" outerRadius={100} label>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const ChartComponent = ct === 'line' ? LineChart : ct === 'area' ? AreaChart : BarChart;
  const DataComponent  = ct === 'line' ? Line      : ct === 'area' ? Area      : Bar;
  return (
    <div className="wrm-report-chart">
      {title && <div className="wrm-report-title">{title}</div>}
      <ResponsiveContainer width="100%" height={280}>
        <ChartComponent data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey={x_field} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <DataComponent dataKey={y_field} fill={CHART_COLORS[0]} stroke={CHART_COLORS[0]} fillOpacity={ct === 'area' ? 0.3 : 1} />
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

const STEP_ICONS = {
  start:          PlayCircleIcon,
  knowledge_base: BookOpenIcon,
  query:          CircleStackIcon,
  api:       GlobeAltIcon,
  webhook:   LinkIcon,
  transform: ArrowsRightLeftIcon,
  llm:       SparklesIcon,
  code:      CodeBracketIcon,
  agent:     CpuChipIcon,
  report:    DocumentChartBarIcon,
  file:      DocumentTextIcon,
  condition: FunnelIcon,
  db_agent:    TableCellsIcon,
  excel_agent:  TableCellsIcon,
  msg_template: ChatBubbleLeftRightIcon,
  report_pdf: DocumentArrowDownIcon,
  end:        StopCircleIcon,
};

/* ─── Agent detail (tool calls + response) ─────────────────────────────── */
const AgentStepDetail = ({ step }) => {
  const isRunning        = step.status === 'running';
  // While running: show live_steps; after done: show final intermediate_steps
  const liveSteps        = step.live_steps || [];
  const intermediateSteps = step.intermediate_steps || [];
  const toolsInvoked      = step.tools_invoked || [];

  const [toolExpanded, setToolExpanded] = useState(() =>
    Object.fromEntries(intermediateSteps.map((_, i) => [i, true]))
  );
  const toggleTool = (i) => setToolExpanded(v => ({ ...v, [i]: !v[i] }));

  return (
    <div className="wrm-agent-detail">
      {toolsInvoked.length > 0 && (
        <div className="wrm-agent-tools-row">
          <WrenchScrewdriverIcon className="wrm-agent-icon" />
          <span className="wrm-agent-tools-label">Tools used:</span>
          {toolsInvoked.map((t, i) => (
            <span key={i} className="wrm-tool-chip">{t}</span>
          ))}
        </div>
      )}

      {/* Live tool calls while agent is running */}
      {isRunning && liveSteps.length > 0 && (
        <div className="wrm-agent-steps">
          {liveSteps.filter(s => s.event === 'tool_start').map((s, i) => (
            <div key={i} className={`wrm-agent-step ${s.done ? 'wrm-agent-step--done' : 'wrm-agent-step--running'}`}>
              <div className="wrm-agent-step-head">
                <WrenchScrewdriverIcon className="wrm-agent-icon" />
                <span className="wrm-agent-step-tool">{s.tool}</span>
                {s.done
                  ? <CheckCircleIcon className="w-3 h-3 text-green-500 ml-auto" />
                  : <span className="wrm-step-running-label ml-auto">calling…</span>
                }
              </div>
              <div className="wrm-agent-step-body">
                <div className="wrm-agent-step-section-label">Input</div>
                <pre className="wrm-agent-step-pre">
                  {typeof s.input === 'object' ? JSON.stringify(s.input, null, 2) : String(s.input)}
                </pre>
                {s.done && s.output && (
                  <>
                    <div className="wrm-agent-step-section-label">Output</div>
                    <pre className="wrm-agent-step-pre">{typeof s.output === 'string' ? s.output : JSON.stringify(s.output, null, 2)}</pre>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Final intermediate steps once done */}
      {!isRunning && intermediateSteps.length > 0 && (
        <div className="wrm-agent-steps">
          {intermediateSteps.map((s, i) => (
            <div key={i} className="wrm-agent-step">
              <div className="wrm-agent-step-head" onClick={() => toggleTool(i)}>
                <WrenchScrewdriverIcon className="wrm-agent-icon" />
                <span className="wrm-agent-step-tool">{s.tool}</span>
                <span className="wrm-agent-step-toggle">{toolExpanded[i] ? '▲' : '▼'}</span>
              </div>
              {toolExpanded[i] && (
                <div className="wrm-agent-step-body">
                  <div className="wrm-agent-step-section-label">Input</div>
                  <pre className="wrm-agent-step-pre">
                    {typeof s.input === 'object' ? JSON.stringify(s.input, null, 2) : String(s.input)}
                  </pre>
                  {s.sub_trace && s.sub_trace.length > 0 ? (
                    <>
                      <div className="wrm-agent-step-section-label">Sub-workflow Steps</div>
                      <div className="wrm-sub-trace">
                        {s.sub_trace.map((st, si) => (
                          <div key={si} className={`wrm-sub-trace-step wrm-sub-trace-step--${st.status || 'success'}`}>
                            <span className="wrm-sub-trace-label">{st.label || st.node_id || st.type}</span>
                            {st.output && (
                              <pre className="wrm-sub-trace-output">
                                {typeof st.output === 'string' ? st.output : JSON.stringify(st.output, null, 2)}
                              </pre>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="wrm-agent-step-section-label">Final Output</div>
                      <pre className="wrm-agent-step-pre">{typeof s.output === 'string' ? s.output : JSON.stringify(s.output, null, 2)}</pre>
                    </>
                  ) : (
                    <>
                      <div className="wrm-agent-step-section-label">Output</div>
                      <pre className="wrm-agent-step-pre">{typeof s.output === 'string' ? s.output : JSON.stringify(s.output, null, 2)}</pre>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {step.output && (
        <div className="wrm-agent-response">
          <div className="wrm-agent-response-label">
            <ChatBubbleLeftRightIcon className="wrm-agent-icon" />
            Agent Response
          </div>
          <pre className="wrm-agent-response-text">{typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

/* ─── Single step row ───────────────────────────────────────────────────── */
const StepRow = ({ step }) => {
  const nodeType  = step.type || 'step';
  // Agent nodes auto-expand to show tool outputs immediately
  const [expanded, setExpanded] = useState(nodeType === 'agent' || step.status === 'partial_error');
  const nodeLabel = step.label || step.node_id || '—';
  const isRunning = step.status === 'running';
  const isAgent   = nodeType === 'agent';
  const isReport    = nodeType === 'report';
  const isReportPdf = nodeType === 'report_pdf';
  const isLoop    = nodeType === 'loop';
  const hasOutput = step.output && String(step.output).trim();
  const hasPreview = step.output_preview && step.output_preview.trim();
  const hasLiveSteps   = isAgent && isRunning && (step.live_steps || []).length > 0;
  const hasAgentDetail = isAgent && (step.output || (step.intermediate_steps || []).length > 0);
  const hasReportData  = isReport && (step.data || []).length > 0;
  const hasPdfUrl   = isReportPdf && step.pdf_url;
  const hasIterTraces = isLoop && (step.iter_traces || []).length > 0;
  const canExpand = !isRunning && (hasOutput || hasPreview || hasAgentDetail || hasReportData || hasPdfUrl || hasIterTraces);
  const StepIcon  = STEP_ICONS[nodeType] || CircleStackIcon;

  return (
    <div className={`wrm-step wrm-step--${step.status || 'done'}`}>
      <div className="wrm-step-head">
        {isRunning
          ? <ArrowPathIcon className="wrm-step-icon-svg wrm-step-icon-spin" />
          : <StepIcon className="wrm-step-icon-svg" />
        }
        <span className="wrm-step-label">{nodeLabel}</span>

        {step.row_count !== undefined && (
          <span className="wrm-row-badge">{step.row_count} rows</span>
        )}
        {step.model && (
          <span className="wrm-model-badge">{step.model}</span>
        )}
        {isAgent && (step.tools_invoked || []).length > 0 && (
          <span className="wrm-tool-badge">
            <WrenchScrewdriverIcon className="w-3 h-3" />
            {step.tools_invoked.length} tool{step.tools_invoked.length !== 1 ? 's' : ''}
          </span>
        )}

        {isRunning
          ? <span className="wrm-step-running-label">running…</span>
          : step.status === 'error'
            ? <ExclamationTriangleIcon className="wrm-step-err-icon" />
            : <CheckCircleIcon className="wrm-step-ok-icon" />
        }
        {step.execution_time !== undefined && (
          <span className="wrm-step-dur">{step.execution_time}s</span>
        )}
        {canExpand && (
          <button className="wrm-step-toggle" onClick={() => setExpanded(v => !v)}>
            {expanded ? '▲' : '▼'}
          </button>
        )}
      </div>

      {/* Inline summary (non-agent, collapsed) */}
      {hasOutput && !isAgent && !expanded && (
        <div className="wrm-step-summary">
          └─ {(typeof step.output === 'string' ? step.output : JSON.stringify(step.output)).substring(0, 120)}{(typeof step.output === 'string' ? step.output : JSON.stringify(step.output)).length > 120 ? '…' : ''}
        </div>
      )}

      {/* Live tool calls while agent is running */}
      {isAgent && isRunning && hasLiveSteps && <AgentStepDetail step={step} />}

      {/* Expanded: agent detail (after completion) */}
      {isAgent && !isRunning && expanded && <AgentStepDetail step={step} />}

      {/* Expanded: report (chart / table) */}
      {isReport && expanded && <ReportOutput step={step} />}

      {/* Expanded: report_pdf download link */}
      {isReportPdf && expanded && step.pdf_url && (
        <div className="wrm-pdf-download">
          <DocumentArrowDownIcon className="wrm-pdf-icon" />
          <a href={step.pdf_url} target="_blank" rel="noopener noreferrer" className="wrm-pdf-link">
            {step.filename || 'Download PDF'}
          </a>
        </div>
      )}

      {/* Expanded: full output (non-agent, non-report, non-pdf) */}
      {hasOutput && !isAgent && !isReport && !isReportPdf && expanded && (
        <pre className="wrm-step-output">{typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}</pre>
      )}

      {/* Expanded: row preview for query nodes */}
      {hasPreview && expanded && (
        <div className="wrm-row-preview">
          <div className="wrm-row-preview-label">Sample rows (first 3):</div>
          <pre className="wrm-step-output wrm-step-output--preview">{step.output_preview}</pre>
        </div>
      )}

      {/* Loop body traces */}
      {isLoop && hasIterTraces && expanded && (
        <div className="wrm-iter-traces">
          {step.iter_traces.map((it, i) => {
            const ItIcon = STEP_ICONS[it.type] || CircleStackIcon;
            const isErr  = it.status === 'error';
            return (
              <div key={i} className={`wrm-iter-step wrm-iter-step--${it.status || 'success'}`}>
                <ItIcon className="wrm-iter-icon" />
                <span className="wrm-iter-label">{it.label || it.type}</span>
                {isErr
                  ? <ExclamationTriangleIcon className="wrm-iter-err-icon" />
                  : <CheckCircleIcon className="wrm-iter-ok-icon" />
                }
                <span className="wrm-iter-output">
                  {isErr ? (it.error || '').substring(0, 160) : (typeof it.output === 'string' ? it.output.substring(0, 100) : '')}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {step.branch_taken && (
        <div className="wrm-step-branch">
          Branch: <strong>{step.branch_taken === 'true' ? '✓ TRUE' : '✗ FALSE'}</strong>
        </div>
      )}
      {step.error && (
        <div className="wrm-step-error-msg">{step.error}</div>
      )}
    </div>
  );
};

/* ─── Webhook info banner ────────────────────────────────────────────────── */
const WebhookInfoBanner = ({ webhookId, webhookName }) => {
  const [copied, setCopied] = useState(false);
  const url = webhookId ? webhookService.getReceiveUrl(webhookId) : '(no webhook configured)';
  const handleCopy = async () => {
    if (!webhookId) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="wrm-webhook-banner">
      <div className="wrm-webhook-banner-title">
        <GlobeAltIcon className="w-4 h-4" />
        <span>Webhook trigger{webhookName ? ` — ${webhookName}` : ''}</span>
      </div>
      {webhookId && (
        <div className="wrm-webhook-url-row">
          <code className="wrm-webhook-url">{url}</code>
          <button className="wrm-webhook-copy" onClick={handleCopy} title="Copy URL">
            {copied
              ? <CheckCircleIcon className="w-3.5 h-3.5 text-green-600" />
              : <ClipboardDocumentIcon className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
      <p className="wrm-webhook-hint">
        POST to this URL from an external service. You can also test-run manually with a payload below.
      </p>
    </div>
  );
};

/* ─── Context Variables panel ────────────────────────────────────────────── */
const ContextVarsPanel = ({ vars }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState('');

  if (!vars || Object.keys(vars).length === 0) return null;

  const copy = (key) => {
    navigator.clipboard.writeText(`{${key}}`);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  // Group: node outputs (_output/_data suffix) vs flat fields
  const nodeVars = Object.entries(vars).filter(([k]) => k.endsWith('_output') || k.endsWith('_data'));
  const fieldVars = Object.entries(vars).filter(([k]) => !k.endsWith('_output') && !k.endsWith('_data'));

  return (
    <div className="wrm-vars-panel">
      <button className="wrm-vars-toggle" onClick={() => setOpen(o => !o)}>
        <ClipboardDocumentIcon className="w-4 h-4" />
        <span>Available Variables <span className="wrm-vars-count">{Object.keys(vars).length}</span></span>
        <span className="wrm-vars-hint">use {'{'}<em>name</em>{'}'} in prompts &amp; queries</span>
        <span className="wrm-vars-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="wrm-vars-body">
          {fieldVars.length > 0 && (
            <div className="wrm-vars-group">
              <div className="wrm-vars-group-title">Payload / Row Fields</div>
              {fieldVars.map(([k, v]) => (
                <div key={k} className="wrm-var-row" onClick={() => copy(k)} title="Click to copy">
                  <code className="wrm-var-name">{`{${k}}`}</code>
                  {copied === k
                    ? <span className="wrm-var-copied">Copied!</span>
                    : <span className="wrm-var-value">{String(v).substring(0, 80)}{String(v).length > 80 ? '…' : ''}</span>}
                </div>
              ))}
            </div>
          )}
          {nodeVars.length > 0 && (
            <div className="wrm-vars-group">
              <div className="wrm-vars-group-title">Node Outputs</div>
              {nodeVars.map(([k, v]) => (
                <div key={k} className="wrm-var-row" onClick={() => copy(k)} title="Click to copy">
                  <code className="wrm-var-name">{`{${k}}`}</code>
                  {copied === k
                    ? <span className="wrm-var-copied">Copied!</span>
                    : <span className="wrm-var-value">{String(v).substring(0, 80)}{String(v).length > 80 ? '…' : ''}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Main modal ────────────────────────────────────────────────────────── */
const WorkflowRunModal = ({ workflow, onClose }) => {
  const triggerType = workflow.trigger_type || 'chat';
  const isWebhook   = triggerType === 'webhook';

  const [inputText, setInputText]   = useState(isWebhook ? '{"event": "test"}' : '');
  const [running, setRunning]       = useState(false);
  const [stepsMap, setStepsMap]     = useState({}); // node_id → step data
  const [stepOrder, setStepOrder]   = useState([]); // ordered node_ids
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError]           = useState('');
  const traceEndRef = useRef(null);

  const scrollToBottom = () =>
    traceEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleRun = async () => {
    if (!isWebhook && !inputText.trim()) { setError('Please provide an input for the workflow.'); return; }
    setRunning(true);
    setError('');
    setStepsMap({});
    setStepOrder([]);
    setFinalResult(null);

    try {
      await workflowService.executeWorkflowStream(
        workflow.workflow_id,
        inputText.trim(),
        (eventType, data) => {
          if (eventType === 'node_start') {
            // Add a new "running" step
            setStepOrder(prev => prev.includes(data.node_id) ? prev : [...prev, data.node_id]);
            setStepsMap(prev => ({
              ...prev,
              [data.node_id]: { ...data, status: 'running' },
            }));
            setTimeout(scrollToBottom, 50);

          } else if (eventType === 'agent_step') {
            // Live tool call update for a running agent node
            setStepsMap(prev => {
              const node = prev[data.node_id] || {};
              const existing = node.live_steps || [];
              let updated;
              if (data.event === 'tool_end') {
                // Merge output into the matching tool_start entry
                updated = existing.map(s =>
                  s.step === data.step && s.event === 'tool_start'
                    ? { ...s, output: data.output, done: true }
                    : s
                );
              } else {
                updated = [...existing, { ...data }];
              }
              return { ...prev, [data.node_id]: { ...node, live_steps: updated } };
            });
            setTimeout(scrollToBottom, 50);

          } else if (eventType === 'node_done') {
            // Replace running step with completed step
            setStepsMap(prev => ({
              ...prev,
              [data.node_id]: data,
            }));
            setTimeout(scrollToBottom, 50);

          } else if (eventType === 'done') {
            setFinalResult(data);
            // Reconcile full trace (catches any steps not emitted via node_done)
            if (data.trace) {
              const traceMap = {};
              const traceOrder = [];
              for (const s of data.trace) {
                const id = s.node_id || s.label || Math.random().toString();
                traceMap[id] = s;
                if (!traceOrder.includes(id)) traceOrder.push(id);
              }
              setStepsMap(traceMap);
              setStepOrder(traceOrder);
            }
            setTimeout(scrollToBottom, 100);

          } else if (eventType === 'error') {
            setError(data.error || 'Unknown error');
          }
        }
      );
    } catch (e) {
      setError(e.message || 'Execution failed');
    } finally {
      setRunning(false);
    }
  };

  const steps    = stepOrder.map(id => stepsMap[id]).filter(Boolean);
  const totalTime = finalResult?.execution_time;
  const hasTrace  = steps.length > 0;

  return (
    <div className="wrm-overlay" onClick={onClose}>
      <div className="wrm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="wrm-header">
          <div className="wrm-header-title">
            <PlayIcon className="w-5 h-5 text-indigo-600" />
            <span>Run Workflow</span>
            <span className="wrm-wf-name">{workflow.name}</span>
          </div>
          <button className="wrm-close" onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Input */}
        <div className="wrm-input-section">
          {isWebhook ? (
            <>
              <WebhookInfoBanner
                webhookId={workflow.webhook_id}
                webhookName={workflow.webhook_name}
              />
              <label>Test Payload <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.75rem' }}>(optional)</span></label>
              <textarea
                className="wrm-input"
                rows={3}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                placeholder='{"issue": {"summary": "Login page broken"}}'
                disabled={running}
              />
            </>
          ) : (
            <>
              <label>Input</label>
              <textarea
                className="wrm-input"
                rows={3}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                placeholder="Enter the input for this workflow…"
                disabled={running}
              />
            </>
          )}
          {error && !running && (
            <div className="wrm-inline-error">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {error}
            </div>
          )}
          <button
            className="wrm-run-btn"
            onClick={handleRun}
            disabled={running}
          >
            {running
              ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Running…</>
              : isWebhook
                ? <><PlayIcon className="w-4 h-4" /> Test Run</>
                : <><PlayIcon className="w-4 h-4" /> Run Workflow</>}
          </button>
        </div>

        {/* Execution trace */}
        {(running || hasTrace) && (
          <div className="wrm-trace-section">
            <div className="wrm-trace-header">
              Execution Trace
              {totalTime !== undefined && (
                <span className="wrm-total-time">
                  <ClockIcon className="w-3.5 h-3.5" /> Total: {totalTime}s
                </span>
              )}
            </div>

            <div className="wrm-trace-list">
              {steps.map((step, i) => (
                <StepRow key={step.node_id || i} step={step} />
              ))}
              <div ref={traceEndRef} />
            </div>

            {/* Context Variables */}
            {finalResult?.context_vars && (
              <ContextVarsPanel vars={finalResult.context_vars} />
            )}

            {/* Final output */}
            {finalResult?.final_output && (() => {
              let parsed = null;
              try { parsed = JSON.parse(finalResult.final_output); } catch (_) {}

              // ── Composite multi-part output (show_in_output nodes) ──
              if (parsed?.__composite__ && Array.isArray(parsed.parts) && parsed.parts.length > 0) {
                return (
                  <div className="wrm-final-output">
                    <div className="wrm-final-label">Result</div>
                    {parsed.parts.map((part, i) => (
                      <div key={i} className="wrm-composite-part">
                        {parsed.parts.length > 1 && part.output_format !== 'text' && (
                          <div className="wrm-composite-part-label">
                            <span className="wrm-composite-part-type">{part.type}</span>
                            {part.label}
                          </div>
                        )}
                        {part.output_format === 'pdf_url' ? (
                          <div className="wrm-pdf-download wrm-pdf-download--featured">
                            <DocumentArrowDownIcon className="wrm-pdf-icon" />
                            <a href={part.url} target="_blank" rel="noopener noreferrer" className="wrm-pdf-link">
                              {part.filename || 'Download PDF'}
                            </a>
                          </div>
                        ) : part.output_format === 'text' ? (
                          <div className="wrm-composite-text"><ReactMarkdown>{part.content || ''}</ReactMarkdown></div>
                        ) : (
                          <ReportOutput step={part} />
                        )}
                      </div>
                    ))}
                  </div>
                );
              }

              // ── Single report output ──
              if (parsed?.render_type) {
                return (
                  <div className="wrm-final-output">
                    <div className="wrm-final-label">Report Output</div>
                    <ReportOutput step={parsed} />
                  </div>
                );
              }

              // ── Plain text ──
              return (
                <div className="wrm-final-output">
                  <div className="wrm-final-label">Final Output</div>
                  <pre className="wrm-final-text">{finalResult.final_output}</pre>
                </div>
              );
            })()}

            {/* Status summary */}
            {finalResult && (
              <div className={`wrm-status-bar wrm-status-bar--${finalResult.status}`}>
                {finalResult.status === 'success'
                  ? <><CheckCircleIcon className="w-4 h-4" /> Workflow completed successfully</>
                  : <><ExclamationTriangleIcon className="w-4 h-4" /> Workflow failed — {finalResult.error || 'Unknown error'}</>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowRunModal;
